import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { FilingOrigin } from '@signalvote/shared';
import type { ComplianceReportDetailDto, SigningEvidenceDto } from '@signalvote/shared';
import { useSigningEvidence } from '@/hooks/queries';
import { useFlashFlag } from '@/hooks';
import { formatAmount, formatDate, formatDateTime } from '../utils';
import { isPostFiling } from './reportUtils';

const COLLAPSE_ABOVE_ROWS = 10;
const SHORT_HASH_LENGTH = 12;

interface EvidenceRow {
  key: string;
  name: string;
  date: string | null;
  amount: number;
}

interface EvidenceGroup {
  label: string;
  rows: EvidenceRow[];
}

/**
 * What the filed package contained when it was generated: who made it, the file's hash, the
 * attestation, the cash on hand, and every row it carried. A treasurer asked "why is this gift on
 * that report?" answers here without reopening the file. Renders nothing before filing and for a
 * report with no package (filed outside the app).
 */
export function SigningEvidencePanel({ report }: { report: ComplianceReportDetailDto }) {
  const hasPackage = isPostFiling(report.status) && report.filingOrigin !== FilingOrigin.ExternalPortal;
  const { data } = useSigningEvidence(hasPackage ? report.id : null);
  if (!hasPackage || !data) return null;

  const pkg = data.submissionPackage;
  const groups = toGroups(data).filter((g) => g.rows.length > 0);
  const rowCount = groups.reduce((n, g) => n + g.rows.length, 0);
  // An attestation typed in the app always carries a name. A whole-report state's generate is
  // never attested here, and its timestamp is the empty 0001-01-01 value, so it is never shown.
  const attested = pkg.attestedByUserNameTyped.trim() !== '';

  return (
    <section
      aria-labelledby="signing-evidence-heading"
      className="rounded-lg border p-4"
      data-testid="signing-evidence-panel"
    >
      <h3 id="signing-evidence-heading" className="text-sm font-semibold">
        What was filed
      </h3>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
        <dt className="text-muted-foreground">Generated</dt>
        <dd>
          {pkg.generatedByUserDisplayName ?? 'A former team member'}, {formatDateTime(pkg.generatedAt)}
        </dd>

        <dt className="text-muted-foreground">File hash</dt>
        <dd className="flex items-center gap-1.5">
          <code className="font-mono text-xs" title={pkg.sha256}>
            {pkg.sha256.slice(0, SHORT_HASH_LENGTH)}
          </code>
          <CopyHashButton hash={pkg.sha256} />
        </dd>

        <dt className="text-muted-foreground">Attestation</dt>
        <dd>
          {attested ? (
            <>
              Signed by {pkg.attestedByUserNameTyped}, {formatDateTime(pkg.attestationCheckedAt)}
            </>
          ) : (
            <span className="text-muted-foreground">Not attested in the app (filed in the state portal)</span>
          )}
        </dd>

        <dt className="text-muted-foreground">Cash on hand</dt>
        <dd className="flex flex-wrap items-baseline gap-x-1.5">
          <span>{formatAmount(pkg.startingCashBalanceAtSign)}</span>
          <span className="text-xs text-muted-foreground">at start</span>
          <span aria-hidden="true">→</span>
          <span>{formatAmount(pkg.endingCashBalanceAtSign)}</span>
          <span className="text-xs text-muted-foreground">at end of period</span>
        </dd>
      </dl>

      {rowCount > 0 && <EvidenceRows groups={groups} rowCount={rowCount} />}
    </section>
  );
}

function EvidenceRows({ groups, rowCount }: { groups: EvidenceGroup[]; rowCount: number }) {
  const [expanded, setExpanded] = useState(rowCount <= COLLAPSE_ABOVE_ROWS);

  return (
    <div className="mt-4 border-t pt-1">
      {rowCount > COLLAPSE_ABOVE_ROWS && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {expanded ? 'Hide the rows' : `Show the ${rowCount} rows`}
        </button>
      )}
      {expanded && (
        // One table for every kind, so names, dates and amounts line up down the whole list.
        <table className="mt-1 w-full max-w-2xl text-sm">
          {groups.map((group) => (
            <tbody key={group.label}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={3}
                  className="pt-3 pb-1 text-left text-xs font-medium text-muted-foreground"
                >
                  {group.label} ({group.rows.length})
                </th>
              </tr>
              {group.rows.map((row) => (
                <tr key={row.key} className="border-b border-border/60 last:border-0">
                  <td className="py-1 pr-3">{row.name}</td>
                  <td className="py-1 pr-3 whitespace-nowrap text-muted-foreground">
                    {row.date ? formatDate(row.date) : null}
                  </td>
                  <td className="py-1 text-right whitespace-nowrap tabular-nums">{formatAmount(row.amount)}</td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      )}
    </div>
  );
}

function toGroups(data: SigningEvidenceDto): EvidenceGroup[] {
  return [
    {
      label: 'Contributions',
      rows: data.contributions.map((c) => ({
        key: c.id,
        name: c.contactName ?? 'Name unavailable',
        date: c.dateAtSign,
        amount: c.amountAtSign,
      })),
    },
    {
      label: 'Expenditures',
      rows: data.expenditures.map((e) => ({
        key: e.id,
        name: e.contactName ?? (e.isSynthesized ? 'Processor fees' : 'Name unavailable'),
        date: e.dateAtSign,
        amount: e.amountAtSign,
      })),
    },
    {
      label: 'Refunds',
      rows: data.refunds.map((r) => ({
        key: r.id,
        name: r.contactName ?? 'Name unavailable',
        date: r.dateAtSign,
        amount: r.amountAtSign,
      })),
    },
    {
      label: 'Loans, balance owed',
      rows: data.loans.map((l) => ({
        key: l.id,
        name: l.contactName ?? 'Name unavailable',
        date: null,
        amount: l.outstandingBalanceAtSign,
      })),
    },
  ];
}

function CopyHashButton({ hash }: { hash: string }) {
  const [copied, flashCopied] = useFlashFlag(1500);
  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(hash);
      flashCopied();
    } catch {
      // Clipboard unavailable (insecure context) or permission denied: a convenience, so stay quiet.
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? 'File hash copied' : 'Copy file hash'}
      title="Copy file hash"
      className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
    </button>
  );
}
